/**
 * PlanDistributionPanel - Visual breakdown of subscriber distribution across plans
 *
 * Features:
 * - Staggered horizontal bar chart per plan with subscriber counts
 * - Recharts donut chart showing proportional distribution
 * - Tier-colored visual encoding (Free=slate, Starter=blue, Pro=violet, Business=emerald)
 * - Clickable rows to view plan subscribers
 * - Framer Motion staggered entrance
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import {
  Card,
  CardContent,
  CardHeader,
  Skeleton,
  cn,
  chartA11yProps,
  pieSegmentA11yProps,
} from '@respectly/ui';
import { Users } from 'lucide-react';
import type { PlanDistribution } from '@respectly/shared-types';

// ============================================
// Tier Configuration
// ============================================

const TIER_CONFIG: Record<number, {
  label: string;
  color: string;       // Tailwind text color
  barColor: string;    // Tailwind bg color for bar fill
  dotColor: string;    // Tailwind bg color for dot
  chartColor: string;  // Hex for Recharts
  badgeBg: string;
  badgeText: string;
}> = {
  0: {
    label: 'Free',
    color: 'text-slate-600 dark:text-slate-400',
    barColor: 'bg-slate-400 dark:bg-slate-500',
    dotColor: 'bg-slate-400 dark:bg-slate-500',
    chartColor: '#94a3b8',
    badgeBg: 'bg-slate-100 dark:bg-slate-800',
    badgeText: 'text-slate-600 dark:text-slate-400',
  },
  1: {
    label: 'Starter',
    color: 'text-blue-600 dark:text-blue-400',
    barColor: 'bg-blue-500 dark:bg-blue-500',
    dotColor: 'bg-blue-500',
    chartColor: '#3b82f6',
    badgeBg: 'bg-blue-50 dark:bg-blue-900/30',
    badgeText: 'text-blue-700 dark:text-blue-400',
  },
  2: {
    label: 'Pro',
    color: 'text-violet-600 dark:text-violet-400',
    barColor: 'bg-violet-500 dark:bg-violet-500',
    dotColor: 'bg-violet-500',
    chartColor: '#8b5cf6',
    badgeBg: 'bg-violet-50 dark:bg-violet-900/30',
    badgeText: 'text-violet-700 dark:text-violet-400',
  },
  3: {
    label: 'Business',
    color: 'text-emerald-600 dark:text-emerald-400',
    barColor: 'bg-emerald-500 dark:bg-emerald-500',
    dotColor: 'bg-emerald-500',
    chartColor: '#10b981',
    badgeBg: 'bg-emerald-50 dark:bg-emerald-900/30',
    badgeText: 'text-emerald-700 dark:text-emerald-400',
  },
};

const FALLBACK_TIER = {
  label: 'Enterprise',
  color: 'text-amber-600 dark:text-amber-400',
  barColor: 'bg-amber-500 dark:bg-amber-500',
  dotColor: 'bg-amber-500',
  chartColor: '#f59e0b',
  badgeBg: 'bg-amber-50 dark:bg-amber-900/30',
  badgeText: 'text-amber-700 dark:text-amber-400',
};

function getTierConfig(tierLevel: number) {
  return TIER_CONFIG[tierLevel] ?? FALLBACK_TIER;
}

// ============================================
// Formatters
// ============================================

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

const numberFormatter = new Intl.NumberFormat('en-US');

// ============================================
// Types
// ============================================

interface PlanDistributionPanelProps {
  plans: PlanDistribution[];
  isLoading: boolean;
  onViewPlanSubscribers?: (planId: string, planName: string) => void;
}

// ============================================
// Custom Donut Tooltip
// ============================================

function DonutTooltip({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number; payload: { mrrContribution: number } }> }) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  if (!d) return null;
  return (
    <div className="rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-slate-900 dark:text-white">{d.name}</p>
      <p className="text-slate-500 dark:text-slate-400 mt-0.5">
        {numberFormatter.format(d.value)} subscriber{d.value !== 1 ? 's' : ''}
      </p>
      <p className="text-slate-500 dark:text-slate-400">
        {currencyFormatter.format(d.payload.mrrContribution)} MRR
      </p>
    </div>
  );
}

// ============================================
// Framer Motion Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.05 },
  },
};

const rowVariants = {
  hidden: { opacity: 0, x: -12 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.25 } },
};

// ============================================
// Component
// ============================================

export function PlanDistributionPanel({
  plans,
  isLoading,
  onViewPlanSubscribers,
}: PlanDistributionPanelProps) {
  const [hoveredPlanId, setHoveredPlanId] = useState<string | null>(null);

  const totalSubscribers = plans.reduce((s, p) => s + p.subscriberCount, 0);
  const totalMrr = plans.reduce((s, p) => s + p.mrrContribution, 0);

  const chartData = plans.map((p) => ({
    name: p.planName,
    value: p.subscriberCount,
    mrrContribution: p.mrrContribution,
    tierLevel: p.tierLevel,
    fill: getTierConfig(p.tierLevel).chartColor,
  }));

  const chartLabel = `Plan distribution across ${numberFormatter.format(totalSubscribers)} subscribers and ${currencyFormatter.format(totalMrr)} monthly recurring revenue: ${plans
    .map(
      (p) =>
        `${p.planName}, ${numberFormatter.format(p.subscriberCount)} subscribers (${p.percentOfTotal.toFixed(1)}%), ${currencyFormatter.format(p.mrrContribution)} MRR`
    )
    .join('; ')}.`;

  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
      {/* Header */}
      <CardHeader className="pb-0 px-6 pt-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center">
              <Users className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                Plan Distribution
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Subscriber breakdown across all plans
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xl font-bold tabular-nums text-slate-900 dark:text-white">
              {numberFormatter.format(totalSubscribers)}
            </p>
            <p className="text-xs text-slate-400">total subscribers</p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-6 pb-5 pt-4">
        {isLoading ? (
          <PlanDistributionPanelSkeleton inline />
        ) : plans.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-center">
            <div>
              <Users className="h-10 w-10 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
              <p className="text-sm text-slate-500 dark:text-slate-400">No plan data available</p>
            </div>
          </div>
        ) : (
          <div className="flex gap-8 items-start">
            {/* ─── Left: Plan rows ─────────────────────────── */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="flex-1 min-w-0 space-y-3"
            >
              {plans.map((plan) => {
                const cfg = getTierConfig(plan.tierLevel);
                const isHovered = hoveredPlanId === plan.planId;
                const barWidth = totalSubscribers > 0
                  ? Math.max(plan.percentOfTotal, plan.subscriberCount > 0 ? 2 : 0)
                  : 0;

                return (
                  <motion.div
                    key={plan.planId}
                    variants={rowVariants}
                    onHoverStart={() => setHoveredPlanId(plan.planId)}
                    onHoverEnd={() => setHoveredPlanId(null)}
                    onClick={() => onViewPlanSubscribers?.(plan.planId, plan.planName)}
                    className={cn(
                      'group rounded-lg px-3 py-2.5 transition-colors duration-150',
                      onViewPlanSubscribers ? 'cursor-pointer' : 'cursor-default',
                      isHovered
                        ? 'bg-slate-50 dark:bg-slate-800/60'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'
                    )}
                  >
                    {/* Plan name + badge + subscriber count */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {/* Tier dot */}
                        <span className={cn('flex-shrink-0 h-2.5 w-2.5 rounded-full', cfg.dotColor)} />

                        {/* Plan name */}
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                          {plan.planName}
                        </span>

                        {/* Tier badge */}
                        <span className={cn(
                          'flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none',
                          cfg.badgeBg, cfg.badgeText
                        )}>
                          {cfg.label}
                        </span>
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                        <span className="text-sm font-bold tabular-nums text-slate-900 dark:text-white">
                          {numberFormatter.format(plan.subscriberCount)}
                        </span>
                        <span className="text-xs text-slate-400 w-10 text-right tabular-nums">
                          {plan.percentOfTotal.toFixed(1)}%
                        </span>
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400 tabular-nums w-16 text-right">
                          {currencyFormatter.format(plan.mrrContribution)}
                        </span>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-700/70 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${barWidth}%` }}
                        transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
                        className={cn('h-full rounded-full', cfg.barColor)}
                      />
                    </div>
                  </motion.div>
                );
              })}

              {/* Totals footer */}
              <div className="pt-2 px-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                  Total across {plans.length} plan{plans.length !== 1 ? 's' : ''}
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold tabular-nums text-slate-900 dark:text-white">
                    {numberFormatter.format(totalSubscribers)}
                  </span>
                  <span className="text-xs text-slate-400 w-10 text-right">100%</span>
                  <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 tabular-nums w-16 text-right">
                    {currencyFormatter.format(totalMrr)}<span className="font-normal text-slate-400"> MRR</span>
                  </span>
                </div>
              </div>
            </motion.div>

            {/* ─── Right: Donut chart ─────────────────────── */}
            <div className="flex-shrink-0 w-[160px] flex flex-col items-center">
              <div className="relative w-[160px] h-[160px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart {...chartA11yProps(chartLabel)}>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={48}
                      outerRadius={72}
                      paddingAngle={2}
                      dataKey="value"
                      strokeWidth={0}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.fill}
                          opacity={hoveredPlanId && hoveredPlanId !== plans[index]?.planId ? 0.4 : 1}
                          style={{ transition: 'opacity 0.15s' }}
                          {...pieSegmentA11yProps(
                            `${entry.name}: ${numberFormatter.format(entry.value)} subscribers, ${currencyFormatter.format(entry.mrrContribution)} MRR`
                          )}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<DonutTooltip />} />
                  </PieChart>
                </ResponsiveContainer>

                {/* Center label */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-xl font-bold tabular-nums text-slate-900 dark:text-white leading-none">
                    {numberFormatter.format(totalSubscribers)}
                  </span>
                  <span className="text-[10px] text-slate-400 mt-0.5">subscribers</span>
                </div>
              </div>

              {/* Legend */}
              <div className="mt-2 space-y-1 w-full">
                {plans.slice(0, 4).map((plan) => {
                  const cfg = getTierConfig(plan.tierLevel);
                  return (
                    <div key={plan.planId} className="flex items-center gap-1.5">
                      <span className={cn('h-2 w-2 rounded-full flex-shrink-0', cfg.dotColor)} />
                      <span className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                        {plan.planName}
                      </span>
                      <span className="text-[10px] font-semibold text-slate-700 dark:text-slate-300 tabular-nums ml-auto">
                        {plan.subscriberCount}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================
// Skeleton
// ============================================

export function PlanDistributionPanelSkeleton({ inline = false }: { inline?: boolean }) {
  const content = (
    <div className="flex gap-8 items-start">
      <div className="flex-1 space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="px-3 py-2.5 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Skeleton className="h-2.5 w-2.5 rounded-full" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-12" />
              </div>
              <div className="flex items-center gap-3">
                <Skeleton className="h-4 w-8" />
                <Skeleton className="h-3 w-8" />
                <Skeleton className="h-3 w-14" />
              </div>
            </div>
            <Skeleton className="h-1.5 w-full rounded-full" />
          </div>
        ))}
      </div>
      <div className="flex-shrink-0 w-[160px] flex flex-col items-center gap-3">
        <Skeleton className="h-[160px] w-[160px] rounded-full" />
        <div className="w-full space-y-1.5">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-3 w-full" />
          ))}
        </div>
      </div>
    </div>
  );

  if (inline) return content;

  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900 shadow-sm">
      <CardHeader className="pb-0 px-6 pt-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Skeleton className="h-8 w-8 rounded-lg" />
            <div className="space-y-1">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
          <div className="text-right space-y-1">
            <Skeleton className="h-6 w-16 ml-auto" />
            <Skeleton className="h-3 w-24 ml-auto" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-6 pb-5 pt-4">{content}</CardContent>
    </Card>
  );
}
